/**
 * Smile.io points block in #quick-buy-content never inits when the template is cloned.
 * Product JSON is embedded in the template; this file builds the same props as the PDP block.
 */
(function () {
  const PLACEHOLDER = '[points_amount]';
  const SMILE_API_HOST = 'platform.smile.io';
  const SMILE_CLIENT = 'smile-shopify-app-extensions';

  let cachedProgramConfiguration = null;

  document.addEventListener('smile-points-on-product-page:preload-completed', (event) => {
    if (event.detail?.render) {
      window.__smilePointsOnProductPageRender = event.detail.render;
    }
  });

  function decodeSmileEncodedString(value) {
    if (!value) {
      return '';
    }

    if (typeof window.safeLiquidToDecodedJsString === 'function') {
      return window.safeLiquidToDecodedJsString(value);
    }

    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  function getSmileHeaders() {
    const locale = (document.documentElement.lang || 'da').split('-')[0];

    return {
      Accept: 'application/json',
      'Smile-Channel-Key': document.querySelector('.smile-shopify-init')?.dataset?.channelKey || '',
      'Smile-Client': SMILE_CLIENT,
      'Content-Type': 'application/json',
      'X-Smile-Language': locale
    };
  }

  function cameliseKey(key) {
    return key.replace(/([-_][a-z])/g, (match) => match.slice(1).toUpperCase());
  }

  function cameliseKeys(value) {
    if (Array.isArray(value)) {
      return value.map(cameliseKeys);
    }

    if (value && typeof value === 'object' && value.constructor === Object) {
      return Object.keys(value).reduce((result, key) => {
        result[cameliseKey(key)] = cameliseKeys(value[key]);
        return result;
      }, {});
    }

    return value;
  }

  function parseSmilePrice(value) {
    if (value == null || value === '') {
      return NaN;
    }

    let str = String(value);
    let thousandsSeparator;

    if (/,\d{3}/.exec(str)) {
      thousandsSeparator = ',';
    } else if (/\.\d{3}/.exec(str)) {
      thousandsSeparator = '\\.';
    }

    if (thousandsSeparator) {
      str = str.replace(new RegExp(thousandsSeparator, 'g'), '');
    }

    return Number(str.replace(/,(\d{2})$/, '.$1'));
  }

  function formatProductPriceForSmile(priceCents) {
    const amount = (priceCents / 100).toFixed(2);
    return amount.replace('.', ',');
  }

  function decodeSmileSsrText(container) {
    container.querySelectorAll('[data-ssr-text]').forEach((element) => {
      element.innerHTML = decodeSmileEncodedString(element.innerHTML);
    });
  }

  function getTargetProduct(container) {
    const productScript = container.querySelector('script[data-smile-quick-buy-product]');

    if (productScript?.textContent) {
      try {
        return JSON.parse(productScript.textContent);
      } catch {
        /* fall through */
      }
    }

    const variantScript = container.querySelector('script[data-variant]');

    if (!variantScript?.textContent) {
      return null;
    }

    try {
      const variant = JSON.parse(variantScript.textContent);
      const handle = container.querySelector('variant-picker')?.getAttribute('handle');

      return {
        id: variant.product_id,
        handle: handle,
        variants: [variant]
      };
    } catch {
      return null;
    }
  }

  function parseSmileBlockConfig(initScript) {
    const text = initScript.textContent;

    const readEncoded = (key) => {
      const match = text.match(
        new RegExp(`${key}:\\s*safeLiquidToDecodedJsString\\("((?:\\\\.|[^"\\\\])*)"`)
      );

      return match ? decodeSmileEncodedString(match[1]) : undefined;
    };

    const readString = (key) => {
      const match = text.match(new RegExp(`${key}:\\s*'([^']*)'`));
      return match ? match[1] : undefined;
    };

    const readBoolean = (key) => {
      const match = text.match(new RegExp(`${key}:\\s*(true|false)`));
      return match ? match[1] === 'true' : undefined;
    };

    return {
      appBlockId: readString('appBlockId'),
      primaryColour: readString('primaryColour'),
      secondaryColour: readString('secondaryColour'),
      shouldShowIcon: readBoolean('shouldShowIcon'),
      shouldShowPointsLabel: readBoolean('shouldShowPointsLabel'),
      previewScenario: readString('previewScenario'),
      isGiftCard: readBoolean('isGiftCard'),
      customImageUrl: readString('customImageUrl'),
      customImageHeightPx: readString('customImageHeight'),
      pointsTextLoggedIn: readEncoded('pointsTextLoggedIn'),
      pointsTextLoggedInDefault: readEncoded('pointsTextLoggedInDefault'),
      pointsTextLoggedOut: readEncoded('pointsTextLoggedOut'),
      pointsTextLoggedOutDefault: readEncoded('pointsTextLoggedOutDefault')
    };
  }

  function waitForSmileRender(maxMs) {
    if (window.__smilePointsOnProductPageRender) {
      return Promise.resolve(window.__smilePointsOnProductPageRender);
    }

    if (!maxMs) {
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      const timeoutId = setTimeout(() => resolve(null), maxMs);

      document.addEventListener(
        'smile-points-on-product-page:preload-completed',
        (event) => {
          clearTimeout(timeoutId);
          resolve(event.detail?.render || null);
        },
        { once: true }
      );
    });
  }

  async function loadProgramConfiguration() {
    if (cachedProgramConfiguration) {
      return cachedProgramConfiguration;
    }

    const channelKey = document.querySelector('.smile-shopify-init')?.dataset?.channelKey;

    if (!channelKey || !window.fetch) {
      return null;
    }

    const headers = getSmileHeaders();

    const programResponse = await fetch(
      `https://${SMILE_API_HOST}/shopify_extensions/init/points_on_product_page?channel_key=${encodeURIComponent(channelKey)}`,
      { method: 'GET', headers }
    );

    if (!programResponse.ok) {
      return null;
    }

    const programData = cameliseKeys(await programResponse.json());
    const accountId = programData.account?.id;

    if (!accountId) {
      return null;
    }

    const rulesResponse = await fetch(
      `https://${SMILE_API_HOST}/shopify_extensions/activity_rules?account_id=${accountId}`,
      { method: 'GET', headers }
    );

    if (!rulesResponse.ok) {
      return null;
    }

    const rulesPayload = cameliseKeys(await rulesResponse.json());

    cachedProgramConfiguration = {
      programData,
      activityRules: rulesPayload.activityRules || []
    };

    return cachedProgramConfiguration;
  }

  async function loadCustomerData() {
    const initElement = document.querySelector('.smile-shopify-init');

    if (!initElement?.dataset?.digest) {
      return { isGuest: true, customer: null };
    }

    const dataset = initElement.dataset;
    const headers = getSmileHeaders();

    const response = await fetch(`https://${SMILE_API_HOST}/shopify_extensions/theme/identify_customer`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        customer: {
          accepts_marketing: dataset.customerAcceptsMarketing,
          email: dataset.customerEmail,
          first_name: dataset.customerFirstName,
          id: dataset.customerId,
          last_name: dataset.customerLastName,
          orders_count: dataset.customerOrdersCount,
          tags: dataset.customerTags,
          total_spent: dataset.customerTotalSpent
        },
        digest: dataset.digest
      })
    });

    if (!response.ok) {
      return { isGuest: true, customer: null };
    }

    const payload = cameliseKeys(await response.json());

    return {
      isGuest: false,
      customer: payload.customer || null
    };
  }

  function getSelectedVariant(targetProduct, container) {
    const variants = targetProduct?.variants || [];
    const variantInput = container.querySelector('input[name="id"]');
    const variantId = Number(variantInput?.value);

    if (variantId) {
      const matched = variants.find((variant) => variant.id === variantId);

      if (matched) {
        return matched;
      }
    }

    const variantScript = container.querySelector('script[data-variant]');

    if (variantScript?.textContent) {
      try {
        return JSON.parse(variantScript.textContent);
      } catch {
        /* ignore */
      }
    }

    return variants[0] || null;
  }

  function buildLiquidVars(blockConfig, targetProduct, container) {
    const selectedVariant = getSelectedVariant(targetProduct, container);

    if (!selectedVariant) {
      return null;
    }

    return {
      ...blockConfig,
      targetProduct,
      productPrice: formatProductPriceForSmile(selectedVariant.price)
    };
  }

  function buildSmileProps(programConfigurationData, customerData, liquidVars, container) {
    const { programData, activityRules } = programConfigurationData;

    if (!programData || !customerData || !liquidVars?.targetProduct) {
      return null;
    }

    const pointsProgram = programData.pointsProgram;
    const customer = customerData.customer;
    const parsedPrice = parseSmilePrice(liquidVars.productPrice);
    const currencyRate = window.Shopify?.currency?.rate || 1;
    const productPrice = currencyRate ? parsedPrice / currencyRate : parsedPrice;
    const selectedVariant = getSelectedVariant(liquidVars.targetProduct, container);
    const isProductDiscounted =
      selectedVariant != null &&
      selectedVariant.compare_at_price != null &&
      selectedVariant.compare_at_price > selectedVariant.price;

    const activeOnlineOrderEarningRules = (activityRules || []).filter(
      (rule) => rule.type === 'shopify_online_order' && rule.isEnabled
    );

    const displaySetting = programData.displaySetting;
    const customerLocale = (document.documentElement.lang || 'da').split('-')[0];
    const programLocale = displaySetting?.customerLocale;

    return {
      primaryColour: liquidVars.primaryColour,
      secondaryColour: liquidVars.secondaryColour,
      shouldShowIcon: liquidVars.shouldShowIcon,
      customImageUrl: liquidVars.customImageUrl,
      customImageHeightPx: liquidVars.customImageHeightPx,
      isPointsProgramEnabled: !!pointsProgram?.isEnabled,
      activeOnlineOrderEarningRules,
      activeActivityRuleBonuses: programData.activeActivityRuleBonuses,
      isInThemeEditor: !!window.Shopify?.designMode,
      productPrice,
      isCustomerExcluded: customer?.state === 'disabled',
      isGuest: !!customerData.isGuest,
      canGuestsEarn: programData.account?.candidateParticipation === 'earn',
      pointsLabelSingular: pointsProgram?.pointsLabelSingular,
      pointsLabelPlural: pointsProgram?.pointsLabelPlural,
      isGiftCard: !!liquidVars.isGiftCard,
      targetProduct: liquidVars.targetProduct,
      excludedShopifyProductIds: programData.excludedShopifyProductIds,
      excludeDiscountedProducts: programData.excludeDiscountedProducts,
      isProductDiscounted,
      previewScenario: liquidVars.previewScenario,
      shouldShowPointsLabel: liquidVars.shouldShowPointsLabel,
      pointsTextLoggedIn: liquidVars.pointsTextLoggedIn,
      pointsTextLoggedInDefault: liquidVars.pointsTextLoggedInDefault,
      pointsTextLoggedOut: liquidVars.pointsTextLoggedOut,
      pointsTextLoggedOutDefault: liquidVars.pointsTextLoggedOutDefault,
      customerLanguageMatchesProgramLanguage:
        !programLocale || customerLocale === programLocale
    };
  }

  function calculatePointsFromRules(productPrice, activityRules) {
    const rule = (activityRules || []).find(
      (entry) => entry.type === 'shopify_online_order' && entry.isEnabled
    );

    if (!rule) {
      return null;
    }

    if (rule.rewardValueType === 'variable') {
      const step = rule.step || 1;
      const multiplier = rule.multiplier || 1;

      return Math.floor((productPrice / step) * multiplier);
    }

    if (rule.rewardValueType === 'fixed') {
      return rule.reward?.value ?? null;
    }

    return null;
  }

  function formatPointsLabel(points, props) {
    const singular = props?.pointsLabelSingular || 'Point';
    const plural = props?.pointsLabelPlural || 'Points';
    const label = points === 1 ? singular : plural;

    if (props?.shouldShowPointsLabel === false) {
      return new Intl.NumberFormat(document.documentElement.lang || 'da').format(points);
    }

    return `${new Intl.NumberFormat(document.documentElement.lang || 'da').format(points)} ${label}`;
  }

  function replacePlaceholder(container, replacement) {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const textNodes = [];

    while (walker.nextNode()) {
      if (walker.currentNode.textContent.includes(PLACEHOLDER)) {
        textNodes.push(walker.currentNode);
      }
    }

    textNodes.forEach((node) => {
      node.textContent = node.textContent.replaceAll(PLACEHOLDER, replacement);
    });
  }

  async function fallbackReplacePlaceholder(container, liquidVars, programConfigurationData, customerData) {
    const props = buildSmileProps(programConfigurationData, customerData, liquidVars, container);

    if (!props) {
      return;
    }

    const points = calculatePointsFromRules(
      props.productPrice,
      props.activeOnlineOrderEarningRules
    );

    if (points == null) {
      return;
    }

    replacePlaceholder(container, formatPointsLabel(points, props));
  }

  async function initSmileBlocksInContainer(container, attempt = 0) {
    decodeSmileSsrText(container);

    const initScript = container.querySelector('script[data-smile-block-init]');

    if (!initScript) {
      return;
    }

    const targetProduct = getTargetProduct(container);

    if (!targetProduct) {
      if (attempt < 5) {
        setTimeout(() => initSmileBlocksInContainer(container, attempt + 1), 200);
      }

      return;
    }

    const blockConfig = parseSmileBlockConfig(initScript);
    const liquidVars = buildLiquidVars(blockConfig, targetProduct, container);

    if (!liquidVars?.appBlockId) {
      return;
    }

    const blockElement = document.getElementById(liquidVars.appBlockId);

    if (!blockElement) {
      return;
    }

    const [programConfigurationData, customerData, render] = await Promise.all([
      loadProgramConfiguration(),
      loadCustomerData(),
      waitForSmileRender(attempt === 0 ? 8000 : 0)
    ]);

    if (!programConfigurationData) {
      return;
    }

    const props = buildSmileProps(programConfigurationData, customerData, liquidVars, container);

    if (!props) {
      return;
    }

    const smileRender = render || window.__smilePointsOnProductPageRender;

    if (!smileRender && attempt < 10) {
      setTimeout(() => initSmileBlocksInContainer(container, attempt + 1), 400);
      return;
    }

    if (smileRender) {
      smileRender(blockElement, props);

      container._smileProps = props;
      container._smileBlockElement = blockElement;
      container._smileRerender = () => {
        smileRender(container._smileBlockElement, container._smileProps);
      };
    } else {
      await fallbackReplacePlaceholder(
        container,
        liquidVars,
        programConfigurationData,
        customerData
      );
    }
  }

  function updateSmilePropsForVariant(container, variant) {
    if (!container?._smileProps || !variant) {
      return;
    }

    const currencyRate = window.Shopify?.currency?.rate || 1;
    const price = variant.price / 100;

    container._smileProps.productPrice = currencyRate ? price / currencyRate : price;
    container._smileProps.isProductDiscounted =
      variant.compare_at_price != null && variant.compare_at_price > variant.price;

    if (container._smileRerender) {
      container._smileRerender();
      return;
    }

    if (container._smileProps.activeOnlineOrderEarningRules) {
      const points = calculatePointsFromRules(
        container._smileProps.productPrice,
        container._smileProps.activeOnlineOrderEarningRules
      );

      if (points != null) {
        replacePlaceholder(container, formatPointsLabel(points, container._smileProps));
      }
    }
  }

  window.hydrateSmilePointsInQuickBuy = function (modal) {
    if (modal) {
      initSmileBlocksInContainer(modal);
    }
  };

  document.addEventListener('dialog:after-show', (event) => {
    if (event.target?.tagName?.toLowerCase() === 'quick-buy-modal') {
      initSmileBlocksInContainer(event.target);
    }
  });

  document.addEventListener('variant:change', (event) => {
    const modal =
      event.target?.closest?.('quick-buy-modal') ||
      document.querySelector('quick-buy-modal[open]');

    if (modal) {
      updateSmilePropsForVariant(modal, event.detail?.variant);
    }
  });

  document.addEventListener('product:rerender', (event) => {
    const modal =
      event.target?.closest?.('quick-buy-modal') ||
      document.querySelector('quick-buy-modal[open]');

    if (!modal) {
      return;
    }

    requestAnimationFrame(() => {
      initSmileBlocksInContainer(modal);
    });
  });
})();
